/**
 * Recursively removes keys from a data object based on specified property chains.
 *
 * @param {Object} data - The input object from which keys will be removed.
 * @param {string[]} propsChainName - An array of property chains. Each chain is a string
 *                                     representing nested keys separated by dots (e.g., "a.b.c").
 * @returns {Object} A new object with the specified keys removed.
 *
 * @example
 * const data = {
 *   a: {
 *     b: {
 *       c: 1,
 *       d: 2
 *     },
 *     e: 3
 *   }
 * };
 * const propsChainName = ['a.b.c', 'a.e'];
 * const result = deepRemoveKeysByPaths(data, propsChainName);
 * // result will be: { a: { b: { d: 2 } } }
 */
export const deepRemoveKeysByPaths = (data, propsChainName) => {
  const output = { ...data }

  const recursiveRemove = (output, keys) => {
    if (!output || typeof data !== 'object') return output

    const [currentKey, ...remainingKeys] = keys

    if (remainingKeys.length === 0) {
      if (output.hasOwnProperty(currentKey)) {
        delete output[currentKey]
      }
    } else {
      if (output[currentKey] && typeof output[currentKey] === 'object') {
        output[currentKey] = recursiveRemove(output[currentKey], remainingKeys)
      }
    }

    return output
  }

  propsChainName.forEach(chainName => {
    if (!chainName) return
    
    const keys = chainName.split('.')
    recursiveRemove(output, keys)
  })

  return output
}
